// The undo stack's ports that bring the editor along by announcing what changed (editorChanges) rather than through its store: material
// instances and presets, scene settings, stories and library items. An undo still running when another scene
// opened announces what only its own scene shows through `announceScene` (gated on that scene), but always
// tells a cache shared across scenes (material rows per asset instance, the preset catalog, the library,
// screen message styles, a story its scenes share), so none of them stays stale after the switch.

import type { components } from '@/generated/contracts/api-v1';
import type { RendererBridge } from '@/modules/rendererBridge';
import { v1 } from '@/infrastructure/api/v1/client';
import { fetchMaterialPresets, fetchShaderLibrary } from '@/modules/materialRegistry/api';
import type { FieldPort, MaterialPort, ScenePort } from './sceneWrites';
import { untracked } from './sceneWrites';
import type { SceneSync } from './scenePorts';
import { purgeDeletions } from './deletions';
import { announce, type EditorChange } from './editorChanges';
import { list, read, remove, removeRow } from './v1Rows';

const ASSET_TYPE_URLS = ['imgUrl', 'sourceUrl', 'optimizedUrl'];

/** Legacy rows hold a missing URL as '', which v1 refuses; undo puts it back as null. */
const emptyUrlsAsNull = (fields: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(fields).map(([key, value]) => [key, ASSET_TYPE_URLS.includes(key) && value === '' ? null : value]));

type Schemas = components['schemas'];
type MaterialInstanceDto = Schemas['MaterialInstanceDto'];
type MaterialTypeDto = Schemas['MaterialTypeDto'];

/** A change only the scene the undo was pressed in should hear. */
export type AnnounceScene = (change: EditorChange) => Promise<void>;

// Its rows are cached per asset instance (the timeline's mask check, the Material panel), whichever scene is open
export const materialsChanged = (assetInstanceId: string): void => announce({ kind: 'materials', assetInstanceId });

/** The port for material instances, kept per node of an asset instance. `bridge` is gated on the undo's scene. */
export function materialPort(bridge: Pick<RendererBridge, 'send'>, sync: SceneSync): MaterialPort & { put(row: MaterialInstanceDto): Promise<MaterialInstanceDto[]> } {
  const material: MaterialPort & { put(row: MaterialInstanceDto): Promise<MaterialInstanceDto[]> } = {
    listFor: async ids => ids.length === 0 ? [] : list(page => v1.GET('/api/v1/material-instances', {
      params: { query: { assetInstanceUniqueId: ids, ...page } },
    })),
    listScene: sceneId => list(page => v1.GET('/api/v1/material-instances', { params: { query: { sceneUniqueId: [sceneId], ...page } } })),
    // Upserting replaces what is on the node: by its preset when the catalog still has it, else by its look
    async put(row) {
      const [presets, looks] = await Promise.all([fetchMaterialPresets(), fetchShaderLibrary()]);
      const preset = row.materialTypeUniqueId ? presets.find(p => p.materialTypeId === row.materialTypeUniqueId) : undefined;
      const look = looks.find(l => l.shaderLibraryId === row.shaderLookUniqueId);
      if (!preset && !look) throw new Error(`Shader ${row.shaderLookUniqueId} is not in the catalog.`);
      return read(v1.POST('/api/v1/material-instances/batch', {
        body: {
          items: [{
            assetInstanceUniqueId: row.assetInstanceUniqueId,
            targetNodeName: row.targetNodeName,
            targetNodeIndex: row.targetNodeIndex,
            unityProperties: row.unityProperties,
            webProperties: row.webProperties,
            webxrProperties: row.webxrProperties,
            swiftProperties: row.swiftProperties,
            ...(preset ? { materialTypeUniqueId: preset.materialTypeId } : { shaderCodeName: look!.codeName }),
          }],
        },
        headers: untracked.headers,
      }));
    },
    async restore(row) {
      for (const saved of await material.put(row as unknown as MaterialInstanceDto)) {
        bridge.send({ type: 'materialInstanceUpserted', materialInstance: saved });
      }
      materialsChanged(String(row.assetInstanceUniqueId));
    },
    async clear(row) {
      const on = (await material.listFor([String(row.assetInstanceUniqueId)])) as MaterialInstanceDto[];
      const current = on.find(m => m.targetNodeName === row.targetNodeName && m.targetNodeIndex === row.targetNodeIndex);
      if (current) {
        const deletion = await remove(v1.DELETE('/api/v1/material-instances/{uniqueId}', { params: { path: { uniqueId: current.uniqueId } }, headers: untracked.headers }));
        bridge.send({ type: 'materialInstanceRemoved', materialInstance: current });
        // Redo puts the material back by its look, never by this deletion
        if (deletion) purgeDeletions([deletion.deletionUniqueId]);
      }
      materialsChanged(String(row.assetInstanceUniqueId));
    },
    async remove(row) {
      const id = String(row.uniqueId);
      const deletion = await remove(v1.DELETE('/api/v1/material-instances/{uniqueId}', { params: { path: { uniqueId: id } }, headers: untracked.headers }));
      if (!deletion) return null;
      bridge.send({ type: 'materialInstanceRemoved', materialInstance: row as unknown as MaterialInstanceDto });
      materialsChanged(String(row.assetInstanceUniqueId));
      await sync.hideDeletion({ ...deletion, deleted: { ...deletion.deleted, materialInstances: deletion.deleted.materialInstances.filter(other => other !== id) } });
      return deletion.deletionUniqueId;
    },
  };
  return material;
}

/** The ports for scene settings, stories, library items, screen message styles and material presets. */
export function rowPorts(announceScene: AnnounceScene, sync: SceneSync): { scene: FieldPort; story: FieldPort; assetType: ScenePort; uiElementType: FieldPort; materialType: ScenePort } {
  const scene: FieldPort = {
    get: sceneId => read(v1.GET('/api/v1/scenes/{sceneUniqueId}', { params: { path: { sceneUniqueId: sceneId } } })),
    async patch(sceneId, fields) {
      const dto = await read(v1.PATCH('/api/v1/scenes/{sceneUniqueId}', { params: { path: { sceneUniqueId: sceneId } }, body: fields, headers: untracked.headers }));
      await announceScene({ kind: 'scene', sceneId });
      return dto;
    },
  };

  const story: FieldPort = {
    get: storyId => read(v1.GET('/api/v1/stories/{storyUniqueId}', { params: { path: { storyUniqueId: storyId } } })),
    async patch(storyId, fields) {
      const dto = await read(v1.PATCH('/api/v1/stories/{storyUniqueId}', { params: { path: { storyUniqueId: storyId } }, body: fields, headers: untracked.headers }));
      // Every scene of the story shows it, the open one perhaps too
      announce({ kind: 'story', storyId });
      return dto;
    },
  };

  const assetType: ScenePort = {
    get: assetTypeId => read(v1.GET('/api/v1/asset-types/{assetTypeUniqueId}', { params: { path: { assetTypeUniqueId: assetTypeId } } })),
    async patch(assetTypeId, fields) {
      const dto = await read(v1.PATCH('/api/v1/asset-types/{assetTypeUniqueId}', { params: { path: { assetTypeUniqueId: assetTypeId } }, body: emptyUrlsAsNull(fields), headers: untracked.headers }));
      announce({ kind: 'libraryItem', assetTypeId, displayName: dto.displayName });
      return dto;
    },
    // Redo deletes a library item as the library did: with its uses, and its stored files once purged
    remove: assetTypeId => removeRow(sync, v1.DELETE('/api/v1/asset-types/{assetTypeUniqueId}', {
      params: { path: { assetTypeUniqueId: assetTypeId }, query: { cascade: true, removeStoredFiles: true } },
      headers: untracked.headers,
    })),
  };

  const uiElementType: FieldPort = {
    get: typeId => read(v1.GET('/api/v1/ui-element-types/{typeUniqueId}', { params: { path: { typeUniqueId: typeId } } })),
    async patch(typeId, fields) {
      const dto = await read(v1.PATCH('/api/v1/ui-element-types/{typeUniqueId}', { params: { path: { typeUniqueId: typeId } }, body: fields, headers: untracked.headers }));
      // The Screen Messages library refetches, as after saving a style
      announce({ kind: 'screenMessageStyle', typeId });
      return dto;
    },
  };

  // v1 lists presets but reads none by id
  const presetById = async (id: string): Promise<MaterialTypeDto> => {
    const found = (await list(page => v1.GET('/api/v1/material-types', { params: { query: page } }))).find(p => p.uniqueId === id);
    if (!found) throw new Error(`Material preset ${id} is gone.`);
    return found;
  };

  const materialType: ScenePort = {
    get: presetById,
    async patch(materialTypeId, fields) {
      const dto = await read(v1.PATCH('/api/v1/material-types/{materialTypeUniqueId}', { params: { path: { materialTypeUniqueId: materialTypeId } }, body: fields, headers: untracked.headers }));
      announce({ kind: 'materialPreset', materialTypeId });
      return dto;
    },
    remove: materialTypeId => removeRow(sync, v1.DELETE('/api/v1/material-types/{materialTypeUniqueId}', { params: { path: { materialTypeUniqueId: materialTypeId } }, headers: untracked.headers })),
  };

  return { scene, story, assetType, uiElementType, materialType };
}
